import { useState, useRef, useEffect, useCallback } from "react";
import { Sparkle, PaperPlaneRight, Copy, Check, User, FloppyDisk, FilePdf } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import API from "@/lib/api";

const QUICK_PROMPTS = [
  "Draft a letter to the client confirming we're instructed and what we need from them.",
  "Summarise the latest document in plain English for the client.",
  "Draft a chaser to the other side's solicitors for outstanding enquiries.",
  "Suggest amendments to the clause I'll paste next.",
];

function MessageBubble({ msg, caseId }) {
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(null); // "docx" | "pdf" | null
  const isUser = msg.role === "user";
  const copy = () => {
    navigator.clipboard.writeText(msg.content);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1500);
  };
  const saveDoc = async (format) => {
    setSaving(format);
    // Title from the first heading-ish/non-empty line.
    const firstLine = (msg.content.split("\n").find((l) => l.trim()) || "AI draft")
      .replace(/[*#>-]/g, "").trim().slice(0, 60) || "AI draft";
    try {
      const { data } = await API.post(`/cases/${caseId}/ai-draft/save-document`, {
        content: msg.content, title: firstLine, format,
      });
      toast.success(`Saved to Documents › AI Drafts: ${data.document?.filename}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save to Documents");
    } finally {
      setSaving(null);
    }
  };
  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`ai-msg-${msg.role}`}>
      <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${isUser ? "bg-slate-200 text-slate-600" : "bg-[#100f5d] text-white"}`}>
        {isUser ? <User weight="fill" className="w-3.5 h-3.5" /> : <Sparkle weight="fill" className="w-3.5 h-3.5" />}
      </div>
      <div className={`group max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${isUser ? "bg-slate-100 text-slate-800" : "bg-white border border-slate-200 text-slate-800"}`}>
        {msg.content}
        {!isUser && (
          <div className="mt-2 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-2">
            <button onClick={copy} data-testid="ai-copy-btn"
              className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-[#100f5d] transition-colors">
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied ? "Copied" : "Copy"}
            </button>
            <button onClick={() => saveDoc("docx")} disabled={!!saving} data-testid="ai-save-docx"
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#100f5d] transition-colors disabled:opacity-50">
              <FloppyDisk className="w-3 h-3" /> {saving === "docx" ? "Saving…" : "Save as Word"}
            </button>
            <button onClick={() => saveDoc("pdf")} disabled={!!saving} data-testid="ai-save-pdf"
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#100f5d] transition-colors disabled:opacity-50">
              <FilePdf className="w-3 h-3" /> {saving === "pdf" ? "Saving…" : "Save as PDF"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AiDraftDialog({ caseId, open, onOpenChange }) {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await API.get(`/cases/${caseId}/ai-draft/history`);
      setMessages(data.messages || []);
      setSessionId(data.session_id || null);
      scrollToBottom();
    } catch {
      /* no history yet */
    }
  }, [caseId]);

  useEffect(() => {
    if (open) loadHistory();
  }, [open, loadHistory]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    scrollToBottom();
    try {
      const { data } = await API.post(`/cases/${caseId}/ai-draft/message`, { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      scrollToBottom();
    } catch (err) {
      toast.error(err.response?.data?.detail || "The assistant couldn't respond. Please try again.");
      setMessages((m) => m.slice(0, -1)); // roll back the unsent user bubble
      setInput(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col" data-testid="ai-draft-panel">
        <SheetHeader className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-[#100f5d] to-[#2a2896]">
          <SheetTitle className="flex items-center gap-2 text-white text-base">
            <Sparkle weight="fill" className="w-5 h-5" /> Draft with AI
          </SheetTitle>
          <p className="text-[12px] text-indigo-100">Grounded on this matter. Always review before use — it can get facts, figures and law wrong.</p>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-slate-50">
          {messages.length === 0 && !busy && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Ask the assistant to draft, summarise or amend something for this case:</p>
              <div className="space-y-2">
                {QUICK_PROMPTS.map((q, i) => (
                  <button key={i} onClick={() => send(q)} data-testid={`ai-quick-${i}`}
                    className="w-full text-left text-[13px] px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-[#100f5d] hover:bg-indigo-50/40 transition-colors text-slate-700">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => <MessageBubble key={i} msg={m} caseId={caseId} />)}
          {busy && (
            <div className="flex gap-2.5" data-testid="ai-thinking">
              <div className="shrink-0 w-7 h-7 rounded-full bg-[#100f5d] text-white flex items-center justify-center">
                <Sparkle weight="fill" className="w-3.5 h-3.5 animate-pulse" />
              </div>
              <div className="bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-400">Drafting…</div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 p-3 bg-white">
          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type an instruction, or paste text to amend…  (Enter to send, Shift+Enter for a new line)"
              rows={2}
              disabled={busy}
              data-testid="ai-draft-input"
              className="flex-1 resize-none text-sm"
            />
            <Button onClick={() => send()} disabled={busy || !input.trim()} data-testid="ai-draft-send"
              className="bg-[#100f5d] hover:bg-[#1a1875] text-white h-10 w-10 p-0 shrink-0">
              <PaperPlaneRight weight="fill" className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
